import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Clock,
  FileText,
  AlertTriangle,
  CheckCircle,
  Shield,
  Calendar as CalendarIcon,
  IndianRupee,
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import complianceRemindersService from "../../../api/services/complianceRemindersService";

const ComplianceReminderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    loadCompliance();
  }, [id]);

  const loadCompliance = async () => {
    try {
      setLoading(true);

      const complianceData = await complianceRemindersService.getComplianceById(
        id
      );
      setCompliance(complianceData);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "text-green-600 bg-green-50";
      case "inactive":
        return "text-gray-600 bg-gray-50";
      case "pending":
        return "text-yellow-600 bg-yellow-50";
      case "overdue":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "pf":
        return "text-blue-600 bg-blue-50";
      case "esi":
        return "text-green-600 bg-green-50";
      case "tds":
        return "text-purple-600 bg-purple-50";
      case "pt":
        return "text-orange-600 bg-orange-50";
      case "lwf":
        return "text-indigo-600 bg-indigo-50";
      case "gratuity":
        return "text-pink-600 bg-pink-50";
      case "bonus":
        return "text-teal-600 bg-teal-50";
      case "other":
        return "text-gray-600 bg-gray-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calculateDaysLeft = (dueDate) => {
    if (!dueDate) return "N/A";
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDaysLeftColor = (days) => {
    if (days === "N/A") return "text-gray-600";
    if (days <= 0) return "text-red-600";
    if (days <= 7) return "text-red-600";
    if (days <= 14) return "text-yellow-600";
    return "text-green-600";
  };


  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    alert("Compliance deleted successfully!");
    navigate("/compliance_reminders");
  };

  const handleBack = () => {
    navigate("/compliance_reminders");
  };

  const handleMarkComplete = async () => {
    try {
      const confirmed = window.confirm(
        `Are you sure you want to mark "${compliance.name}" as complete? This action cannot be undone.`
      );

      if (!confirmed) return;

      // Update local state to show completion
      setCompliance((prev) => ({
        ...prev,
        status: "completed",
        lastFilingDate: new Date().toISOString(),
        updatedBy: "Current User",
        updatedAt: new Date().toISOString(),
      }));

      alert("✅ Compliance marked as complete successfully!");
      navigate("/compliance_reminders");
    } catch (error) {
      alert("❌ Error marking compliance as complete. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading compliance details...</p>
        </div>
      </div>
    );
  }

  if (!compliance) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="p-3 bg-red-100 rounded-full mb-4 mx-auto w-fit">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Compliance Not Found
          </h3>
          <p className="text-gray-600 mb-4">
            The compliance you're looking for doesn't exist or has been deleted.
          </p>
          <Button
            variant="primary"
            onClick={handleBack}
            icon={<ArrowLeft className="h-4 w-4" />}
          >
            Back to Compliance Reminders
          </Button>
        </div>
      </div>
    );
  }

  const daysLeft = calculateDaysLeft(compliance.nextDueDate);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={handleBack}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {compliance.name}
            </h1>
            <p className="text-gray-600">Compliance Details</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="primary"
            icon={<CheckCircle className="h-4 w-4" />}
            onClick={handleMarkComplete}
            disabled={compliance?.status === "completed"}
            className={
              compliance?.status === "completed"
                ? "bg-green-600 hover:bg-green-700"
                : ""
            }
          >
            {compliance?.status === "completed" ? "Completed" : "Mark Complete"}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-blue-50 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Basic Information
                </h2>
                <p className="text-sm text-gray-600">Core compliance details</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  {compliance.name}
                </h3>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${getTypeColor(
                    compliance.type
                  )}`}
                >
                  {compliance.type.toUpperCase()}
                </span>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${getStatusColor(
                    compliance.status
                  )}`}
                >
                  {compliance.status.toUpperCase()}
                </span>
              </div>

              <p className="text-gray-600">{compliance.description}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Compliance ID</p>
                  <p className="font-medium text-gray-900">
                    {compliance.complianceId}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Type</p>
                  <p className="font-medium text-gray-900">{compliance.type}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <p className="font-medium text-gray-900">
                    {compliance.status}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Effective Date</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(compliance.effectiveDate)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Next Due Date</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(compliance.nextDueDate)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Days Left</p>
                  <p className={`font-medium ${getDaysLeftColor(daysLeft)}`}>
                    {daysLeft === "N/A" ? "N/A" : `${daysLeft} days`}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Created By</p>
                  <p className="font-medium text-gray-900">
                    {compliance.createdBy}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Updated By</p>
                  <p className="font-medium text-gray-900">
                    {compliance.updatedBy}
                  </p>
                </div>
                {compliance.lastFilingDate && (
                  <div>
                    <p className="text-gray-500">Last Filing Date</p>
                    <p className="font-medium text-green-600">
                      {formatDate(compliance.lastFilingDate)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Forms Information */}
          {compliance.forms && compliance.forms.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-green-50 rounded-lg">
                  <FileText className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Required Forms
                  </h2>
                  <p className="text-sm text-gray-600">
                    Forms needed for compliance
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {compliance.forms.map((form, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {form.name}
                        </h4>
                        <p className="text-xs text-gray-500">
                          Code: {form.code}
                        </p>
                      </div>
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                        {form.frequency}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-gray-500">Due Date (Day of Month)</p>
                        <p className="font-medium text-gray-900">
                          {form.dueDate}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Frequency</p>
                        <p className="font-medium text-gray-900">
                          {form.frequency}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Additional Info & Actions */}
        <div className="space-y-6">

          {/* Compliance Timeline */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Clock className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Timeline
                </h2>
                <p className="text-sm text-gray-600">
                  Important dates and events
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <CalendarIcon className="h-4 w-4 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Created</p>
                  <p className="text-xs text-gray-500">
                    {formatDateTime(compliance.createdAt)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <CalendarIcon className="h-4 w-4 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Effective Date
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDate(compliance.effectiveDate)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <CalendarIcon className="h-4 w-4 text-yellow-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Next Due Date
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDate(compliance.nextDueDate)}
                  </p>
                </div>
              </div>
              {compliance.lastFilingDate && (
                <div className="flex items-center space-x-3">
                  <CalendarIcon className="h-4 w-4 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Last Filing
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(compliance.lastFilingDate)}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-center space-x-3">
                <CalendarIcon className="h-4 w-4 text-gray-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Last Updated
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDateTime(compliance.updatedAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Compliance Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-indigo-50 rounded-lg">
                <Shield className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Status Overview
                </h2>
                <p className="text-sm text-gray-600">
                  Current compliance status
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status</span>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${getStatusColor(
                    compliance.status
                  )}`}
                >
                  {compliance.status.toUpperCase()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Active</span>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${compliance.isActive
                      ? "text-green-600 bg-green-50"
                      : "text-red-600 bg-red-50"
                    }`}
                >
                  {compliance.isActive ? "YES" : "NO"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Days Left</span>
                <span
                  className={`text-sm font-medium ${getDaysLeftColor(
                    daysLeft
                  )}`}
                >
                  {daysLeft === "N/A" ? "N/A" : `${daysLeft} days`}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete Compliance
                </h3>
                <p className="text-sm text-gray-600">
                  This action cannot be undone
                </p>
              </div>
            </div>

            <p className="text-gray-700 mb-6">
              Are you sure you want to delete "{compliance.name}"? This will
              permanently remove the compliance and all associated data.
            </p>

            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="bg-red-600 hover:bg-red-700"
                onClick={confirmDelete}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceReminderDetail;
